import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ThriveGameModule } from '../../../sdk';
import { loadAndValidate, type GameLoadFailure } from '../../../registry';
import { GameStage } from './GameStage';

/**
 * Launch path for `/play/:gameId` (architecture §9). The module is lazily imported and
 * validated before anything is rendered; an invalid or missing game becomes a readable screen,
 * never a blank one and never an exception.
 */

type HostState =
  | { status: 'loading'; gameId: string | undefined }
  | { status: 'ready'; gameId: string; gameModule: ThriveGameModule }
  | { status: 'failed'; gameId: string; failure: GameLoadFailure };

function FailureScreen({ failure }: { failure: GameLoadFailure }) {
  return (
    <section className="error-screen" role="alert">
      <h1>That game will not start</h1>
      <p className="lede">{failure.message}</p>
      <p>
        <Link to="/catalogue" className="button button--primary">
          Back to games
        </Link>
      </p>
      {failure.validation === undefined ? null : (
        <details>
          <summary>Details for a grown-up</summary>
          <pre>
            {failure.validation.errors.map((error) => `${error.code}: ${error.message}`).join('\n')}
          </pre>
        </details>
      )}
    </section>
  );
}

export function GameHostPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [loaded, setLoaded] = useState<HostState>({ status: 'loading', gameId });

  useEffect(() => {
    if (gameId === undefined) return;

    let cancelled = false;

    void loadAndValidate(gameId)
      .then((result) => {
        if (cancelled) return;
        setLoaded(
          result.ok
            ? { status: 'ready', gameId, gameModule: result.module }
            : { status: 'failed', gameId, failure: result },
        );
      })
      .catch((cause: unknown) => {
        // `loadAndValidate` is written not to reject, but a stranded "Getting things ready…"
        // is the one outcome a child must never be left with, so the page carries its own net.
        if (cancelled) return;
        setLoaded({
          status: 'failed',
          gameId,
          failure: {
            ok: false,
            gameId,
            reason: 'INVALID_MODULE',
            message: `Loading "${gameId}" failed unexpectedly.`,
            validation: {
              valid: false,
              errors: [
                {
                  code: 'LAUNCH_REJECTED',
                  message: cause instanceof Error ? cause.message : String(cause),
                },
              ],
              warnings: [],
            },
          },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Derive rather than reset in the effect: a different game in the URL is "loading" until
  // its own module has come back, without an extra render pass to get there.
  const state: HostState = loaded.gameId === gameId ? loaded : { status: 'loading', gameId };

  if (gameId === undefined) {
    return (
      <section className="error-screen" role="alert">
        <h1>No game was chosen</h1>
        <Link to="/catalogue" className="button button--primary">
          Back to games
        </Link>
      </section>
    );
  }

  if (state.status === 'loading') {
    return (
      <section data-testid="game-loading">
        <h1>Getting things ready…</h1>
        <p className="lede">Loading {gameId}.</p>
      </section>
    );
  }

  if (state.status === 'failed') return <FailureScreen failure={state.failure} />;

  return <GameStage gameModule={state.gameModule} />;
}
